import {
  BackgroundColors,
  NegativeColors,
  PositiveColors,
  PrimaryColors,
  Shadows,
  TextColors,
} from '@picsart/design-system/foundation/colors';

import { createUseStyles } from 'react-jss';

import { Easing } from '@picsart/design-system/foundation/easing';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { Timings } from '@picsart/design-system/foundation/timings';
import { ColorSchemes } from 'redesignedComponents/context/colorScheme/enums';

import type { ColorSchemeType } from 'redesignedComponents/context/colorScheme/types';

const useStyles = createUseStyles<string, { colorScheme: ColorSchemeType }>(
  {
    root: {
      background: ({ colorScheme }) => {
        return colorScheme === ColorSchemes.DARK
          ? NegativeColors.base.default
          : PositiveColors.base.default;
      },
      minHeight: 320,
      marginBottom: Spacings.s64,
      marginTop: Spacings.s64,
      overflow: 'hidden',
      '@media screen and (max-width: 768px)': {
        marginBottom: Spacings.s0,
      },
    },
    title: {
      textAlign: 'center',
      color: ({ colorScheme }) => {
        return colorScheme === ColorSchemes.DARK ? NegativeColors.text.default : TextColors.base;
      },

      '@media screen and (max-width: 768px)': {
        '-webkit-line-clamp': 4,
        minWidth: 250,
      },
    },
    carousel: {
      marginBottom: Spacings.s44,

      '& > div[class^="wrapper"]': {
        overflow: 'visible',
      },

      '& $carouselContainer': {
        overflow: 'visible',
        paddingBottom: Spacings.s32,
        paddingInline: Spacings.s32,
        '@media screen and (max-width: 768px)': {
          padding: Spacings.s0,
        },
      },
    },
    carouselContainer: {
      '& > $carouselDots': {
        margin: [Spacings.s0, 'auto', Spacings.s48],
      },

      '& $control': {
        '& svg': {
          '& path': {
            fill: NegativeColors.base.default,
          },
        },
        '&.prev': {
          left: `calc(${Spacings.s32} * -1)`,

          '@media screen and (max-width: 766px)': {
            display: 'none',
          },
          '@media screen and (max-width: 768px)': {
            alignItems: 'flex-start',
            top: 100,
            left: 30,
          },
          '& > button': {
            backgroundColor: PositiveColors.base.default,
            width: 48,
            height: 48,
            borderRadius: CornerRadius.circle,
            boxShadow: Shadows.tint1,
            '@media screen and (max-width: 766px)': {
              width: 36,
              height: 36,
            },
          },
        },

        '&.next': {
          right: `calc(${Spacings.s32} * -1)`,

          '@media screen and (max-width: 766px)': {
            display: 'none',
          },
          '@media screen and (max-width: 768px)': {
            alignItems: 'flex-start',
            top: 100,
            right: 30,
          },
          '& > button': {
            backgroundColor: PositiveColors.base.default,
            width: 48,
            height: 48,
            borderRadius: CornerRadius.circle,
            boxShadow: Shadows.tint1,
            '@media screen and (max-width: 766px)': {
              width: 36,
              height: 36,
            },
          },
        },
      },

      '& $dots': {
        margin: [Spacings.s32, 'auto', Spacings.s32],
      },
    },
    dots: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: [Spacings.s0, 'auto'],

      '& > button': {
        height: 8,
        width: 8,
        margin: [Spacings.s0, Spacings.s12, Spacings.s0, Spacings.s0],
        backgroundColor: BackgroundColors.tint3,

        '&.active': {
          backgroundColor: PrimaryColors.base.active,
        },
      },
    },
    control: {
      display: 'flex',
      alignItems: 'center',
      transition: `all ${Timings.default} ${Easing.easeInOut}`,

      '& > button': {
        width: 28,
        height: 56,
        borderRadius: CornerRadius.r8,
        backgroundColor: BackgroundColors.tint2,

        '& svg': {
          width: 16,
          height: 16,

          '& > path': {
            fill: NegativeColors.base.activeAction,
          },
        },
      },

      '&.disabled': {
        visibility: 'hidden',
        opacity: 0,

        '&.prev': {
          transform: 'translateX(-10px)',
        },

        '&.next': {
          transform: 'translateX(10px)',
        },
      },
    },
    skeletonRoot: {
      margin: [Spacings.s0, 'auto', Spacings.s32, 'auto'],
      width: '100%',
      maxWidth: 1320,
    },
    skeletonItemTitle: {
      width: 556,
      height: 288,
      marginBottom: Spacings.s24,
      '@media screen and (max-width: 1400px)': {
        maxWidth: 350,
      },
    },
    skeletonItemDescription: {
      width: 556,
      height: 140,
      '@media screen and (max-width: 1400px)': {
        maxWidth: 350,
      },
    },
    skeletonItemImage: {
      height: 500,
      width: 400,
      borderRadius: 50,
      '@media screen and (max-width: 1400px)': {
        height: 400,
      },
      '@media screen and (max-width: 768px)': {
        height: 174,
      },
    },
    skeletonWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: 20,
      '@media screen and (max-width: 768px)': {
        flexWrap: 'wrap-reverse',
        justifyContent: 'center',
      },
    },
    skeletonTitle: {
      margin: [Spacings.s24, 'auto'],
      minHeight: 32,
      maxWidth: 400,
    },
  },
  {
    name: 'banner-block-carousel',
  },
);

export default useStyles;
